import React, { useEffect, useState } from "react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import { fetchRecordsBreakdown } from "../../../services/dashboardService"; // Adjust path if needed

const COLORS = ["#2563eb", "#6366f1", "#a21caf", "#eab308"];

export default function RecordsDonutChart() {
  const [period, setPeriod] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [data, setData] = useState([
    { name: "Expenses", value: 0 },
    { name: "Reports", value: 0 },
    { name: "Bills", value: 0 },
    { name: "Proformas", value: 0 }
  ]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      setError(null);
      try {
        const result = await fetchRecordsBreakdown({
          period,
          startDate: period === "range" ? startDate : undefined,
          endDate: period === "range" ? endDate : undefined,
        });
        
        // Extract data from the response wrapper
        const recordsData = result.data || result;
        
        setData([
          { name: "Expenses", value: recordsData.expenses || 0 },
          { name: "Reports", value: recordsData.reports || 0 },
          { name: "Bills", value: recordsData.bills || 0 },
          { name: "Proformas", value: recordsData.proformas || 0 }
        ]);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    if (period === "range" && (!startDate || !endDate)) return;
    fetchData();
  }, [period, startDate, endDate]);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 w-full">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-semibold text-gray-900">Records Breakdown</h2>
        <div className="flex items-center gap-2">
          <select
            className="px-3 py-1.5 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            value={period}
            onChange={e => {
              setPeriod(e.target.value);
              if (e.target.value !== 'range') {
                setStartDate("");
                setEndDate("");
              }
            }}
          >
            <option value="all">All Time</option>
            <option value="7days">Last 7 Days</option>
            <option value="30days">Last 30 Days</option>
            <option value="quarter">Last Quarter</option>
            <option value="year">Last Year</option>
            <option value="range">Custom Range</option>
          </select>
          {period === "range" && (
            <>
              <input
                type="date"
                className="px-2 py-1 border rounded"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
                placeholder="Start date"
                max={endDate || undefined}
                required
              />
              <span className="mx-1">to</span>
              <input
                type="date"
                className="px-2 py-1 border rounded"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
                placeholder="End date"
                min={startDate || undefined}
                required
              />
            </>
          )}
        </div>
      </div>
      {/* Chart Visualization */}
      <div className="h-64 w-full">
        {loading ? (
          <div className="h-56 flex items-center justify-center text-gray-500">Loading chart...</div>
        ) : error ? (
          <div className="h-56 flex items-center justify-center text-red-500">{error}</div>
        ) : data.every(item => item.value === 0) ? (
          <div className="h-56 flex items-center justify-center text-gray-500">
            No records found for the selected period
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={90}
                fill="#8884d8"
                paddingAngle={2}
                label
              >
                {data.map((entry, idx) => (
                  <Cell key={`cell-${idx}`} fill={COLORS[idx % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        )}
      </div>
      {/* Dynamic Chart Legend */}
      <div className="mt-6 flex flex-wrap gap-4">
        {data.map((entry, idx) => (
          <div key={entry.name} className="flex items-center">
            <div className="h-3 w-3 rounded-full mr-2" style={{backgroundColor: COLORS[idx % COLORS.length]}}></div>
            <span className="text-sm text-gray-600">{entry.name}: {entry.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
